/**
 * Helper functions for authentication
 */

import { supabase } from './supabase-auth'

/**
 * Check if a user has MFA enabled by checking user metadata and database
 * 
 * @param userId - The user ID to check
 * @param userMetadata - Optional user metadata from auth session (for efficiency)
 * @param forceRefresh - Force refresh from auth API to get latest metadata
 */
export async function checkUserMFAStatus(
  userId: string, 
  userMetadata?: any,
  forceRefresh: boolean = false
): Promise<{
  hasMFA: boolean
  isEnabled: boolean
  needsSetup: boolean
}> {
  try {
    // CRITICAL FIX: For login flow, we should ALWAYS refresh to get latest metadata
    // The JWT from signInWithPassword might have stale metadata
    if (forceRefresh || !userMetadata) {
      // Get fresh user data from Supabase Auth API
      // This will have the most up-to-date metadata from the database
      const { data: { user: freshUser }, error: refreshError } = await supabase.auth.getUser()
      
      if (!refreshError && freshUser) {
        // Check the refreshed metadata
        const metadata = freshUser.user_metadata || {}
        const hasMFA = !!(metadata.mfa_enabled && metadata.totp_secret)
        const isEnabled = !!metadata.mfa_enabled
        
        if (import.meta.env.DEV) {
          console.log('🔐 MFA Status from refreshed user (most accurate):', { 
            hasMFA, 
            isEnabled, 
            hasSecret: !!metadata.totp_secret,
            setupDate: metadata.mfa_setup_date,
            email: freshUser.email,
            userId: freshUser.id
          })
        }
        
        return {
          hasMFA,
          isEnabled,
          needsSetup: isEnabled && !metadata.totp_secret
        }
      }
    }
    
    // Check provided metadata if available and not forcing refresh
    if (userMetadata) {
      const hasMFA = !!(userMetadata.mfa_enabled && userMetadata.totp_secret)
      const isEnabled = !!userMetadata.mfa_enabled
      
      if (import.meta.env.DEV) {
        console.log('🔐 MFA Status from provided metadata:', { 
          hasMFA, 
          isEnabled, 
          hasSecret: !!userMetadata.totp_secret,
          setupDate: userMetadata.mfa_setup_date,
          metadata: userMetadata
        })
      }
      
      return {
        hasMFA,
        isEnabled,
        needsSetup: isEnabled && !userMetadata.totp_secret
      }
    }

    // Fallback: Get current user metadata
    const { data: currentUser } = await supabase.auth.getUser()
    if (currentUser?.user) {
      // Check both user_metadata and raw_user_meta_data
      const metadata = currentUser.user.user_metadata || {}
      
      // CRITICAL FIX: Also check for app_metadata and raw_user_meta_data
      // The save-totp-secret edge function saves to user_metadata, which appears in the JWT
      const hasMFA = !!(metadata.mfa_enabled && metadata.totp_secret)
      const isEnabled = !!metadata.mfa_enabled
      
      if (import.meta.env.DEV) {
        console.log('🔐 MFA Status from current user:', { 
          hasMFA, 
          isEnabled, 
          hasSecret: !!metadata.totp_secret,
          user_metadata: currentUser.user.user_metadata,
          app_metadata: currentUser.user.app_metadata,
          email: currentUser.user.email
        })
      }
      
      return {
        hasMFA,
        isEnabled,
        needsSetup: isEnabled && !metadata.totp_secret
      }
    }

    // Fallback: Check client_users table for client accounts
    const { data: clientData } = await supabase
      .from('client_users')
      .select('two_factor_enabled, two_factor_secret, two_factor_verified_at')
      .eq('auth_user_id', userId)  // Fixed: use auth_user_id instead of user_id
      .single()

    if (clientData) {
      return {
        hasMFA: !!clientData.two_factor_secret,
        isEnabled: clientData.two_factor_enabled || false,
        needsSetup: !clientData.two_factor_secret && clientData.two_factor_enabled !== false
      }
    }

    // Check mfa_preferences table for enforcement settings
    const { data: mfaPrefs } = await supabase
      .from('mfa_preferences')
      .select('enforce_2fa, grace_period_ends')
      .eq('user_id', userId)
      .single()

    if (mfaPrefs && mfaPrefs.enforce_2fa) {
      // If MFA is enforced but user doesn't have it set up
      const gracePeriodExpired = mfaPrefs.grace_period_ends ? 
        new Date(mfaPrefs.grace_period_ends) < new Date() : false
      
      return {
        hasMFA: false,
        isEnabled: false,
        needsSetup: gracePeriodExpired  // Force setup if grace period expired
      }
    }

    // Default: no MFA
    return {
      hasMFA: false,
      isEnabled: false,
      needsSetup: false
    }
  } catch (error) {
    console.error('Error checking MFA status:', error)
    // On error, fall back to safe default (no MFA)
    return {
      hasMFA: false,
      isEnabled: false,
      needsSetup: false
    }
  }
}

/**
 * Clear MFA from user metadata after recovery
 */
export async function clearUserMFAMetadata(userId: string): Promise<boolean> {
  try {
    const { error } = await supabase.auth.updateUser({
      data: {
        totp_secret: null,
        totp_verified: null
      }
    })

    if (error) {
      console.error('Error clearing MFA metadata:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('Error clearing MFA metadata:', error)
    return false
  }
}